import { useState, useRef } from 'react';
import { Card, Spin, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import FeatherIcon from 'feather-icons-react';
import { InputFromUser } from '../Input/input';
import { SubmitButton } from '../Button';
import SectionCard from '../appointment-details/SectionCard';
import { openPrintPreview } from '../Print';
import './PatientFormDisplay.css';

export const PatientFormDisplay = ({ patientData, onSubmit, viewOnly = false }) => {
  const [loader, setLoader] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const formikRef = useRef();

  if (!patientData?.personalDetails) {
    return <div>No patient data available</div>;
  }

  // Get the personal details array
  const personalDetails = patientData.personalDetails || [];

  // Get gender to filter female-specific fields
  // Prefer top-level patient data, fallback to personalDetails field
  const currentGender =
    patientData.gender || personalDetails.find(f => f.personalDetilsKey === 'gender')?.value;

  // Group fields by QuestionType
  const groupedFields = personalDetails.reduce((acc, field) => {
    const category = field.QuestionType || 'Other';

    // Check if field should be shown based on 'for' property
    const showField = !field.for || field.for === currentGender || currentGender === 'Other';

    if (showField) {
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(field);
    }

    return acc;
  }, {});

  const SECTION_CONFIG = {
    PersonalDetails: { title: 'Personal Details', icon: 'user', color: '#4f46e5' },
    BodyAnalysis: { title: 'Body Analysis', icon: 'activity', color: '#ec4899' },
    LifeStyleAnalysis: { title: 'Life Style Analysis', icon: 'wind', color: '#10b981' },
    MentalHealth: { title: 'Mental Health', icon: 'smile', color: '#f59e0b' },
    Other: { title: 'Other Information', icon: 'file-text', color: '#6b7280' },
  };

  const getSectionInfo = category => {
    return SECTION_CONFIG[category] || { title: category, icon: 'list', color: '#6b7280' };
  };

  // Calculate age from date of birth
  const calculateAge = dobString => {
    if (!dobString) return '';
    const dob = new Date(dobString);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to sanitize a value - ensures it's never a plain object
  const sanitizeValue = (value, fieldKey) => {
    // Allow null, undefined, strings, numbers, booleans
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
      return value;
    // Allow arrays (for multiple select fields)
    if (Array.isArray(value)) return value;
    // If it's a plain object, log and return empty string
    if (typeof value === 'object') {
      console.warn('Found object value in field:', fieldKey, value);
      return '';
    }
    return value;
  };

  // Create a deep copy of personalDetails to avoid mutating Redux state
  const initialFormData = personalDetails.map(item => {
    // Create a clean copy of each field with all necessary properties
    const cleanItem = {
      index: item.index,
      title: item.title,
      personalDetilsKey: item.personalDetilsKey,
      type: item.type,
      value: sanitizeValue(item.value, item.personalDetilsKey),
      QuestionType: item.QuestionType,
      Option: item.Option,
      required: item.required,
      placeholder: item.placeholder,
      validation: item.validation,
      for: item.for,
      disabled: item.disabled,
    };
    return cleanItem;
  });

  const handlePrint = () => {
    // Determine which data to use: current form values (if available) or initial data
    const currentValues = formikRef.current?.values?.personalDetils;
    const dataToPrint = currentValues || initialFormData;

    // Group fields by QuestionType for printing
    const printGroups = dataToPrint.reduce((acc, field) => {
      const category = field.QuestionType || 'Other';
      // Check if field should be shown based on gender logic
      const showField = !field.for || field.for === currentGender || currentGender === 'Other';

      if (showField) {
        if (!acc[category]) {
          acc[category] = [];
        }

        let displayValue = sanitizeValue(field.value, field.personalDetilsKey) || '—';

        // Format date values to DD-MM-YYYY
        if (field.type === 'date' && field.value) {
          const d = dayjs(field.value);
          if (d.isValid()) {
            displayValue = d.format('DD/MM/YYYY');
          }
        }

        // Add to the group
        acc[category].push({
          label: field.title,
          value: displayValue,
          key: field.personalDetilsKey,
        });
      }
      return acc;
    }, {});

    // Convert grouped data to sections format required by PrintPreview
    const printSections = Object.keys(printGroups).map(category => {
      const sectionInfo = getSectionInfo(category);
      return {
        title: sectionInfo.title,
        fields: printGroups[category],
      };
    });

    const patientName = patientData.name || patientData.fullName || 'Patient';

    openPrintPreview({}, `${patientName} - Assessment Form`, {
      sections: printSections,
    });
  };

  return (
    <div className="patient-form-display" style={{ position: 'relative' }}>
      {loader && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      )}

      <Card className="patient-form-main-card" style={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
        {/* Patient Header Info - Clickable Toggle */}
        <div
          onClick={() => setIsOpen(!isOpen)}
          style={{
            marginBottom: isOpen ? '12px' : '0',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            padding: '8px 16px',
            boxShadow: '0 2px 6px rgba(79, 70, 229, 0.1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}
        >
          <div style={{ width: '20px' }} /> {/* Spacer to center title */}
          <h3
            style={{
              margin: 0,
              color: '#ffffff',
              textAlign: 'center',
              fontSize: '16px',
              fontWeight: '700',
              letterSpacing: '0.3px',
              flex: 1
            }}
          >
            Detailed Health Assessment Form
          </h3>

          <Tooltip title="Print/Download Form">
            <div
              onClick={e => {
                e.stopPropagation();
                handlePrint();
              }}
              className="print-action-btn"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                cursor: 'pointer',
              }}
            >
              <FeatherIcon icon="printer" size="18" style={{ color: '#ffffff' }} />
            </div>
          </Tooltip>
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FeatherIcon
              icon={isOpen ? "chevron-up" : "chevron-down"}
              size="16"
              style={{ color: '#ffffff' }}
            />
          </div>
        </div>

        {/* Editable Form with Formik - Only shown when open */}
        {isOpen && (
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={{
              personalDetils: initialFormData,
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setLoader(true);
              setSubmitting(true);

              // Prepare the payload with specific structure
              const selectedPersonalDetils = values.personalDetils;

              const payload = {
                personalDetails: selectedPersonalDetils,
              };

              try {
                if (onSubmit) {
                  const result = onSubmit(payload);
                  // Check if the result is a Promise
                  if (result && typeof result.then === 'function') {
                    await result;
                  } else {
                    // If not a promise, wait a bit to show the loader
                    await new Promise(resolve => setTimeout(resolve, 1000));
                  }
                } else {
                  // No onSubmit provided, just show loader for demo
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              } catch (error) {
                console.error('Form submission error:', error);
              } finally {
                setLoader(false);
                setSubmitting(false);
              }
            }}
          >
            {({ values, errors, touched, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {
              return (
                <form onSubmit={handleSubmit}>
                  {/* Form Sections with SectionCard components */}
                  <div className="patient-form-sections">
                    {Object.keys(groupedFields).map((category, index) => {
                      const sectionInfo = getSectionInfo(category);
                      const fields = groupedFields[category];

                      return (
                        <SectionCard
                          key={category}
                          title={sectionInfo.title}
                          icon={sectionInfo.icon}
                          badge={fields.length}
                          badgeColor={sectionInfo.color}
                          defaultOpen={index === 0} // Only open the first section by default
                        >
                          <div className="row">
                            {fields.map(field => {
                              // Find the original index in the personalDetails array
                              const originalIndex = personalDetails.findIndex(
                                p => p.personalDetilsKey === field.personalDetilsKey
                              );
                              // Get the current value from form values (not the original field)
                              let currentField = values?.personalDetils?.[originalIndex] || field;

                              // Sanitize: ensure value is never a plain object
                              const sanitizedValue = sanitizeValue(
                                currentField.value,
                                currentField.personalDetilsKey
                              );
                              if (sanitizedValue !== currentField.value) {
                                console.error(
                                  'Object value found in field:',
                                  currentField.personalDetilsKey,
                                  'Resetting to:',
                                  sanitizedValue
                                );
                                currentField = {
                                  ...currentField,
                                  value: sanitizedValue,
                                };
                              }

                              return (
                                <InputFromUser
                                  key={field.personalDetilsKey}
                                  label={currentField.title}
                                  type={currentField.type}
                                  name={currentField.personalDetilsKey}
                                  onChange={v => {
                                    let filteredValue;

                                    // Handle different input types
                                    if (
                                      currentField.type === 'text' ||
                                      currentField.type === 'number' ||
                                      currentField.type === 'textarea'
                                    ) {
                                      // For text, number and textarea inputs, extract value from event
                                      filteredValue = v?.target?.value ?? v;
                                    } else if (currentField.type === 'date') {
                                      // For date (Ant Design DatePicker), value is passed directly as string
                                      filteredValue = v;
                                    } else if (
                                      currentField.type === 'multiple' ||
                                      currentField.type === 'singleSelect'
                                    ) {
                                      // For select types, value is passed directly
                                      filteredValue = v;
                                    } else if (
                                      currentField.type === 'radio' ||
                                      currentField.type === 'buttonGroup'
                                    ) {
                                      // For radio/buttonGroup, value is passed directly
                                      filteredValue = v;
                                    } else {
                                      // Default: try to extract from event, fallback to direct value
                                      filteredValue = v?.target?.value ?? v;
                                    }

                                    // Sanitize the value to ensure it's never an object
                                    filteredValue = sanitizeValue(
                                      filteredValue,
                                      currentField.personalDetilsKey
                                    );

                                    const personalDetailsList = values?.personalDetils || [];
                                    // Create a new object preserving all field properties
                                    const newField = {
                                      index: currentField.index,
                                      title: currentField.title,
                                      personalDetilsKey: currentField.personalDetilsKey,
                                      type: currentField.type,
                                      value: filteredValue,
                                      QuestionType: currentField.QuestionType,
                                      Option: currentField.Option,
                                      required: currentField.required,
                                      placeholder: currentField.placeholder,
                                      validation: currentField.validation,
                                      for: currentField.for,
                                      disabled: currentField.disabled,
                                    };
                                    const newList = [...(personalDetailsList || [])];
                                    newList[originalIndex] = newField;
                                    setFieldValue('personalDetils', newList);

                                    // Auto-calculate age when DOB changes
                                    if (currentField.personalDetilsKey === 'dob') {
                                      const age = calculateAge(filteredValue);
                                      const ageIndex = personalDetails.findIndex(
                                        p => p.personalDetilsKey === 'age'
                                      );
                                      if (ageIndex !== -1) {
                                        const ageField = newList[ageIndex];
                                        const ageValue = {
                                          ...ageField,
                                          value: age,
                                        };
                                        const newListWithAge = [...newList];
                                        newListWithAge[ageIndex] = ageValue;
                                        setFieldValue('personalDetils', newListWithAge);
                                      }
                                    }
                                  }}
                                  onBlur={handleBlur}
                                  FormControlClassName={'form-control'}
                                  FormGroupClassName={
                                    currentField.type === 'textarea'
                                      ? 'col-12'
                                      : 'col-12 col-md-6 col-lg-4'
                                  } // Responsive column layout
                                  LabelClassName={'col-form-label'}
                                  error={errors?.name}
                                  disabled={viewOnly || currentField.disabled === true}
                                  touched={touched.name}
                                  // For radio and buttonGroup, use selectedValue
                                  {...(currentField.type === 'radio' || currentField.type === 'buttonGroup'
                                    ? {
                                      selectedValue: currentField.value,
                                      options: currentField.Option?.map(v => ({
                                        label: v.title,
                                        value: v.title,
                                      })),
                                    }
                                    : {
                                      // For text, date, select types, use value
                                      value: currentField.value,
                                      options: currentField.Option,
                                    })}
                                  placeholder={'Please fill ' + currentField.title}
                                />
                              );
                            })}
                          </div>
                        </SectionCard>
                      );
                    })}
                  </div>

                  {/* Submit Button - Hidden in view-only mode */}
                  {!viewOnly && (
                    <div className="col-12">
                      <div className="submit-button-container">
                        <SubmitButton htmlType="submit" loading={isSubmitting || loader} className="submit-btn" style={{ margin: 0 }}>
                          {isSubmitting || loader ? 'Submitting...' : 'Submit'}
                        </SubmitButton>
                      </div>
                    </div>
                  )}
                </form>
              );
            }}
          </Formik>
        )}
      </Card>
    </div>
  );
};
